import React, { useState, useEffect, useContext } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import SimpleBar from "simplebar-react";
import addVolunteerStyle from "../css/volunteer.module.scss";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import profileImage from "../../../../assets/images/volunteer.png";
import { VolunteerFormValues } from "modules/Volunteer/MasterVolunteer/helper/volunteer.types";
import { getFileIcon, normalizeVolunteer, renderValue, VolunteerFields, VolunteerSections } from "../helper/volunteer.constant";
import { BreadcrumbContext } from "../../../../Layouts/Header";
import { VOLUNTEER_PATHS } from "Routes/paths";
import TruncatedText from "Components/Common/TruncatedText";
import { formatDate } from "common/service/CommonService";


interface BreadcrumbItem {
  text: string;
  link?: string;
}

interface ViewVolunteerDetailsProps {
  volunteerData: VolunteerFormValues;
  isLoading: boolean;
  breadcrumbItems?: BreadcrumbItem[];
}

const ViewVolunteerDetails: React.FC<ViewVolunteerDetailsProps> = ({
  volunteerData,
  isLoading: loading,
  breadcrumbItems,
}) => {
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  volunteerData = normalizeVolunteer(volunteerData);
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;


  useEffect(() => {
    const defaultBreadcrumbs: BreadcrumbItem[] = [
      { text: "Volunteers", link: VOLUNTEER_PATHS.LIST },
      { text: "Volunteer Details" },
    ];

    const itemsToSet = breadcrumbItems || defaultBreadcrumbs;

    setBreadcrumbItems?.(itemsToSet);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  const onViewAttachment = (filePath: string) => {
    const API_FILE_BASE_URL =  sessionStorage.getItem("filebaseUrl") || process.env.REACT_APP_FILE_BASE_URL
    const baseUrl = API_FILE_BASE_URL;
    
    setPreviewFile(`${baseUrl}${filePath}`);
    setPreviewOpen(true);
  };

  return (
    <>
      <Container fluid className="px-3">
        <Row
          className={`mb-3 ${addVolunteerStyle.add_volunteer_wrapper}  ${addVolunteerStyle.view_volunteer_wrapper}  gap-2`}
        >
          {/* ================= LEFT SECTION ================= */}
          <Col lg={2} className={`p-0 ${addVolunteerStyle.left_section}`}>
            <Card className={addVolunteerStyle.card_wrapper}>
              <CardBody className="text-center">
                {/* Profile */}
                <div className={addVolunteerStyle.profile_wrapper}>
                  <div className={addVolunteerStyle.avatar_wrapper}>
                    {loading ? (
                      <div className="placeholder-glow">
                        <div
                          className="placeholder rounded-circle m-2"
                          style={{ width: 90, height: 90 }}
                        ></div>
                      </div>
                    ) :
                      (<img
                        src={
                          volunteerData?.profileimg
                            ? `${sessionStorage.getItem("filebaseUrl") ?? process.env.REACT_APP_FILE_BASE_URL}${volunteerData.profileimg}`
                            : profileImage
                        }
                        alt="Volunteer"
                        className={addVolunteerStyle.avatar_image}
                      />)}
                  </div>
                  <div className={addVolunteerStyle.profile_info}>
                    <div className={addVolunteerStyle.profile_name}>
                      {loading ? (
                        <span className="placeholder col-8"></span>
                      ) : (
                        volunteerData?.fullname
                      )}
                    </div>

                    <div className={addVolunteerStyle.profile_code}>
                      <span className={addVolunteerStyle.code_label}>
                        Volunteer Code
                      </span>

                      <span className={addVolunteerStyle.code_badge}>
                        {loading ? (
                          <span
                            className="placeholder"
                            style={{
                              width: "70px",
                              height: "14px",
                              display: "inline-block",
                            }}
                          />
                        ) : (
                          volunteerData?.volunteercode
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <hr className="my-2" />

                {/* Signature */}
                <div className={addVolunteerStyle.signature_wrapper}>
                  <h6 className="text-start">Signature</h6>

                  <div className={addVolunteerStyle.signature_box}>
                    {loading ? (
                      <span
                        className="placeholder w-100 h-100"
                        style={{ display: "block", minHeight: "50px" }}
                      ></span>
                    ) : volunteerData?.signature ? (
                      <img
                        src={`${sessionStorage.getItem("filebaseUrl") ?? process.env.REACT_APP_FILE_BASE_URL}${volunteerData.signature}`}
                        className={addVolunteerStyle.signature_image}
                      />
                    ) : (
                      <div className={addVolunteerStyle.signature_placeholder}>
                        No Signature
                      </div>
                    )}
                  </div>
                </div>

                {/* Retina */}
                <div className={addVolunteerStyle.retina_wrapper}>
                  <div className={addVolunteerStyle.retina_header}>
                    <h6 className="mb-0">Retina Scan</h6>
                  </div>

                  <div className={addVolunteerStyle.retina_box}>
                    {loading ? (
                      <span
                        className="placeholder w-100 h-100"
                        style={{ display: "block", minHeight: "50px" }}
                      ></span>
                    ) : volunteerData?.retinaImage ? (
                      <img
                        src={volunteerData.retinaImage}
                        className="img-fluid"
                      />
                    ) : (
                      <div className={addVolunteerStyle.retina_placeholder}>
                        No Retina Data
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* ================= RIGHT SECTION ================= */}
          <Col lg={10} className={`${addVolunteerStyle.right_section} p-0`}>
            <Card className={addVolunteerStyle.card_wrapper}>
              <CardBody
                className="p-0"
                style={{
                  height: "calc(100vh - 133px)",
                  overflow: "hidden",
                }}
              >
                <SimpleBar style={{ height: "100%" }} className="p-3">
                  {/* Sections */}
                  {VolunteerSections.map((section, index) => (
                    <div
                      key={index}
                      className={addVolunteerStyle.section_wrapper}
                    >
                      <div className={addVolunteerStyle.section_header}>
                        {section.title}
                      </div>

                      <Row className="g-3">
                        {loading
                          ? Array.from({ length: 6 }).map((_, i) => (
                            <Col md={4} key={i}>
                              <div className="placeholder-glow">
                                <span className="placeholder col-6 mb-1"></span>
                                <span className="placeholder col-12"></span>
                              </div>
                            </Col>
                          ))
                          : VolunteerFields.filter(
                            (field) =>
                              section.fields.includes(field.name) &&
                              field.name !== "alcoholresult" &&
                              field.name !== "bac" &&
                              field.name !== "indicatorlightcolor"
                          ).map((field: any) => (
                            <Col md={4} key={field.name}>
                              { }
                              <strong className="d-block mb-1">
                                {field.label}
                              </strong>
                              <small className="text-muted d-block">
                                {(() => {
                                  const value = volunteerData?.[field.name as keyof VolunteerFormValues];
                                  if (value === null || value === undefined || value === "") {
                                    return renderValue(value);
                                  }

                                  const displayValue = field.name === "dob" || field.type === "date" ? formatDate(value as string) : renderValue(value);

                                  return (
                                    <TruncatedText
                                      text={String(displayValue)}
                                      maxWidth="100%"
                                    />
                                  );
                                })()}
                              </small>
                            </Col>
                          ))}
                      </Row>
                    </div>
                  ))}

                  {/* ================= ATTACHMENTS ================= */}
                  <div className={addVolunteerStyle.section_wrapper}>
                    <div className={addVolunteerStyle.section_header}>
                      Attachments
                    </div>

                    <Row className="g-3">
                      {loading ? (
                        Array.from({ length: 3 }).map((_, i) => (
                          <Col md={4} key={i}>
                            <div className={addVolunteerStyle.attachmentCard}>
                              <div className="placeholder-glow">
                                <span className="placeholder col-8 mb-2"></span>
                                <span className="placeholder col-4"></span>
                              </div>
                            </div>
                          </Col>
                        ))
                      ) : volunteerData?.attachments?.length === 0 ? (
                        <div className="text-muted small">
                          No attachments available.
                        </div>
                      ) : (
                        volunteerData?.attachments?.map((item: any) => (
                          <Col md={4} key={item.volunteerattachmentid}>
                            <div className={addVolunteerStyle.attachmentCard}>
                              <div className={addVolunteerStyle.fileInfo}>
                                <div className={addVolunteerStyle.fileIcon}>
                                  <i
                                    className={getFileIcon(
                                      item.name?.split(".").pop(),
                                    )}
                                  />
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className={addVolunteerStyle.fileName}>
                                    <TruncatedText text={item.name} />
                                  </div>
                                  <div className={addVolunteerStyle.fileMeta}>
                                    {item.size}
                                  </div>
                                  <div className="small text-muted">
                                    Type : {(item.attachmentTypetext || item.attachmenttypetext || "").toUpperCase()}
                                  </div>
                                </div>
                              </div>

                              <div className={addVolunteerStyle.fileActions}>
                                <i
                                  className="ri-eye-line"
                                  title="Preview"
                                  onClick={() => onViewAttachment(item.path)}
                                />
                              </div>
                            </div>
                          </Col>
                        ))
                      )}
                    </Row>
                  </div>
                </SimpleBar>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <FilePreviewModal
        fileUrl={previewFile}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

export default ViewVolunteerDetails;
